import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { ChevronDown, MessageSquare, History, PanelLeftClose, PanelLeftOpen } from 'lucide-react'
import { CORE_ITEMS, NAV_GROUPS, ALL_NAV_ITEMS, MANTASTIC_ITEM, AUTHORITY_ITEM, CEO_WEEKLY_ITEM, type NavItem, type NavGroup } from './nav-config'
import { useNavBadges, type NavBadge, type NavBadgeMap } from '@/hooks/use-nav-badges'
import { useRecents } from '@/hooks/use-recents'
import { isOwner } from '@/lib/auth-role'
import { cn } from '@/lib/utils'

/** localStorage-backed boolean that stays in sync across the tree. */
function usePersistentBoolean(key: string, initial: boolean): [boolean, () => void] {
  const [value, setValue] = useState<boolean>(() => {
    if (typeof localStorage === 'undefined') return initial
    const raw = localStorage.getItem(key)
    return raw == null ? initial : raw === 'true'
  })
  const toggle = useCallback(() => {
    setValue((v) => {
      const next = !v
      try {
        localStorage.setItem(key, String(next))
      } catch {
        // ignore unavailable storage
      }
      return next
    })
  }, [key])
  return [value, toggle]
}

function Badge({ badge, floating }: { badge: NavBadge; floating?: boolean }) {
  return (
    <span
      className={cn(
        'rounded-full px-1.5 py-0.5 text-[10px] font-semibold leading-none tabular-nums',
        floating
          ? 'absolute -right-0.5 -top-0.5 min-w-[15px] text-center'
          : 'mr-2',
        badge.tone === 'soft' ? 'bg-sky-soft text-primary' : 'bg-bad text-white',
      )}
      aria-label={badge.label}
    >
      {badge.count}
    </span>
  )
}

function NavItemLink({
  item,
  badge,
  collapsed,
  onNavigate,
}: {
  item: NavItem
  badge?: NavBadge
  collapsed?: boolean
  onNavigate?: () => void
}) {
  const { to, label, icon: Icon } = item
  return (
    <NavLink
      to={to}
      end={to === '/' || to === '/mantastic'}
      onClick={onNavigate}
      title={collapsed ? label : undefined}
      className={({ isActive }) =>
        cn(
          'relative flex items-center rounded-lg py-2 text-sm transition-colors duration-micro',
          collapsed ? 'justify-center px-0' : 'gap-3 pl-3',
          isActive
            ? 'bg-sky-soft font-medium text-primary'
            : 'text-foreground hover:bg-sky-soft hover:text-head',
        )
      }
    >
      <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
      {!collapsed && <span className="flex-1">{label}</span>}
      {badge && (collapsed ? <Badge badge={badge} floating /> : <Badge badge={badge} />)}
    </NavLink>
  )
}

/** Chat is an action, not a route — it opens the assistant sidebar. */
function ChatButton({ collapsed, onOpenChat }: { collapsed?: boolean; onOpenChat?: () => void }) {
  return (
    <button
      type="button"
      onClick={onOpenChat}
      title={collapsed ? 'Chat' : undefined}
      className={cn(
        'flex w-full items-center rounded-lg py-2 text-sm text-foreground transition-colors duration-micro hover:bg-sky-soft hover:text-head',
        collapsed ? 'justify-center px-0' : 'gap-3 pl-3',
      )}
    >
      <MessageSquare className="h-4 w-4 shrink-0" aria-hidden="true" />
      {!collapsed && (
        <>
          <span className="flex-1 text-left">Chat</span>
          <kbd className="mr-2 rounded border border-border px-1 py-0.5 text-[9px] font-medium text-muted-foreground">
            ⌘J
          </kbd>
        </>
      )}
    </button>
  )
}

function GroupSection({
  group,
  badges,
  onNavigate,
}: {
  group: NavGroup
  badges: NavBadgeMap
  onNavigate?: () => void
}) {
  const [open, toggle] = usePersistentBoolean(`friday.nav.group.${group.id}`, group.defaultOpen ?? false)
  const panelId = `nav-group-${group.id}`
  return (
    <div className="mt-4 flex flex-col gap-0.5">
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        aria-controls={panelId}
        className="flex items-center gap-1 px-3 pb-1 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground transition-colors duration-micro hover:text-foreground"
      >
        <ChevronDown
          className={cn('h-3 w-3 shrink-0 transition-transform', !open && '-rotate-90')}
          aria-hidden="true"
        />
        <span className="flex-1 text-left">{group.label}</span>
      </button>
      {open && (
        <div id={panelId} className="flex flex-col gap-0.5">
          {group.items.map((item) => (
            <NavItemLink key={item.to} item={item} badge={badges[item.to]} onNavigate={onNavigate} />
          ))}
        </div>
      )}
    </div>
  )
}

function RecentsSection({ onNavigate }: { onNavigate?: () => void }) {
  const recents = useRecents()
  if (recents.length === 0) return null
  return (
    <div className="mt-4 flex flex-col gap-0.5">
      <p className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
        Recent
      </p>
      {recents.map((r) => (
        <NavLink
          key={`${r.type}:${r.id}`}
          to={r.path}
          onClick={onNavigate}
          className={({ isActive }) =>
            cn(
              'flex items-center gap-3 rounded-lg py-1.5 pl-3 text-xs transition-colors duration-micro',
              isActive
                ? 'text-head'
                : 'text-foreground hover:bg-sky-soft hover:text-head',
            )
          }
        >
          <History className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span className="flex-1 truncate">{r.name}</span>
        </NavLink>
      ))}
    </div>
  )
}

/**
 * The nav body shared by the desktop rail ({@link Sidebar}) and the mobile
 * drawer. `collapsed` renders the icon rail (desktop only) — badges survive it.
 */
export function SidebarNav({
  onNavigate,
  onOpenChat,
  collapsed = false,
  onToggleCollapse,
}: {
  onNavigate?: () => void
  onOpenChat?: () => void
  collapsed?: boolean
  onToggleCollapse?: () => void
}) {
  const badges = useNavBadges()
  // Mantastic is the owner's personal program — gated at the nav surface.
  const owner = isOwner()

  // The active pill travels between items rather than teleporting (same trick
  // as the segmented tabs' thumb). Measured off [aria-current=page]; when
  // measurement is unavailable the link's own active background is the
  // fallback, so the rail never renders without an active state.
  const location = useLocation()
  const bodyRef = useRef<HTMLDivElement>(null)
  const [pill, setPill] = useState<{ top: number; height: number } | null>(null)
  useLayoutEffect(() => {
    const el = bodyRef.current
    if (!el) return
    const update = () => {
      const active = el.querySelector<HTMLElement>('[aria-current="page"]')
      if (active && active.offsetHeight > 0) {
        setPill({ top: active.offsetTop, height: active.offsetHeight })
      } else {
        setPill(null)
      }
    }
    update()
    // Group expand/collapse and badge changes move the active item without a
    // route change — watch the subtree, not just the location.
    const mo = new MutationObserver(update)
    mo.observe(el, { subtree: true, childList: true, attributes: true, attributeFilter: ['aria-current'] })
    let ro: ResizeObserver | undefined
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(update)
      ro.observe(el)
    }
    return () => {
      mo.disconnect()
      ro?.disconnect()
    }
  }, [collapsed, location.pathname])

  // Icon rail: core + Chat + any other item that carries a whisper badge, so
  // badges survive the collapse. Nav order is preserved; Mantastic joins for the owner.
  const railExtras = [
    ...ALL_NAV_ITEMS.filter((item) => badges[item.to] && !CORE_ITEMS.some((c) => c.to === item.to)),
    ...(owner ? [AUTHORITY_ITEM, CEO_WEEKLY_ITEM, MANTASTIC_ITEM] : []),
  ]

  const handleChat = () => {
    onOpenChat?.()
    onNavigate?.()
  }

  return (
    <>
      {/* Logo */}
      <div
        className={cn(
          'flex h-14 items-center border-b border-border',
          collapsed ? 'justify-center px-0' : 'gap-2.5 px-4',
        )}
      >
        <img
          src="/icons/icon-192.png"
          alt="FRIDAY"
          className="h-7 w-7 flex-none object-contain"
        />
        {!collapsed && <span className="font-semibold tracking-tight text-head">FRIDAY</span>}
      </div>

      {/* Nav body */}
      <div
        ref={bodyRef}
        className={cn(
          'relative flex flex-1 flex-col overflow-y-auto p-3',
          collapsed && 'items-stretch',
          pill && '[&_[aria-current=page]]:bg-transparent',
        )}
      >
        {pill && (
          <span
            aria-hidden
            className="absolute left-3 right-3 rounded-lg bg-sky-soft transition-[transform,height] duration-small ease-fluid motion-reduce:transition-none"
            style={{ height: pill.height, transform: `translateY(${pill.top - 12}px)`, top: 12 }}
          />
        )}
        {/* Pinned core */}
        <div className="flex flex-col gap-0.5">
          {CORE_ITEMS.map((item) => (
            <NavItemLink
              key={item.to}
              item={item}
              badge={badges[item.to]}
              collapsed={collapsed}
              onNavigate={onNavigate}
            />
          ))}
          <ChatButton collapsed={collapsed} onOpenChat={handleChat} />
        </div>

        {collapsed ? (
          railExtras.length > 0 && (
            <div className="mt-4 flex flex-col gap-0.5 border-t border-border pt-3">
              {railExtras.map((item) => (
                <NavItemLink
                  key={item.to}
                  item={item}
                  badge={badges[item.to]}
                  collapsed
                  onNavigate={onNavigate}
                />
              ))}
            </div>
          )
        ) : (
          <>
            {NAV_GROUPS.map((group) => (
              <GroupSection key={group.id} group={group} badges={badges} onNavigate={onNavigate} />
            ))}
            {owner && (
              <div className="mt-4 flex flex-col gap-0.5 border-t border-border pt-3">
                <NavItemLink item={AUTHORITY_ITEM} badge={badges[AUTHORITY_ITEM.to]} onNavigate={onNavigate} />
                <NavItemLink item={CEO_WEEKLY_ITEM} onNavigate={onNavigate} />
                <NavItemLink item={MANTASTIC_ITEM} badge={badges[MANTASTIC_ITEM.to]} onNavigate={onNavigate} />
              </div>
            )}
            <RecentsSection onNavigate={onNavigate} />
          </>
        )}
      </div>

      {/* Collapse toggle — desktop rail only */}
      {onToggleCollapse && (
        <div className="border-t border-border p-3">
          <button
            type="button"
            onClick={onToggleCollapse}
            aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
            title={collapsed ? 'Expand [' : 'Collapse ['}
            className={cn(
              'flex w-full items-center rounded-lg py-2 text-xs text-muted-foreground transition-colors duration-micro hover:bg-sky-soft hover:text-head',
              collapsed ? 'justify-center px-0' : 'gap-2 pl-3',
            )}
          >
            {collapsed ? (
              <PanelLeftOpen className="h-4 w-4 shrink-0" aria-hidden="true" />
            ) : (
              <PanelLeftClose className="h-4 w-4 shrink-0" aria-hidden="true" />
            )}
            {!collapsed && (
              <span className="flex-1 text-left">
                Collapse <kbd className="ml-1 text-muted-foreground">[</kbd>
              </span>
            )}
          </button>
        </div>
      )}
    </>
  )
}

/** Desktop navigation rail. Hidden below `md`, where the drawer takes over. */
export function Sidebar({ onOpenChat }: { onOpenChat?: () => void }) {
  const [collapsed, toggleCollapsed] = usePersistentBoolean('friday.nav.collapsed', false)

  // `[` toggles the icon rail, unless the user is typing.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== '[' || e.metaKey || e.ctrlKey || e.altKey) return
      const el = e.target as HTMLElement | null
      const tag = el?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || el?.isContentEditable) return
      e.preventDefault()
      toggleCollapsed()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [toggleCollapsed])

  return (
    <nav
      aria-label="Main navigation"
      className={cn(
        // Floating glass rail — follows the theme (navy no longer the wallpaper).
        // Width is the one accepted layout transition (single element).
        'glass-chrome my-3 ml-3 hidden shrink-0 flex-col overflow-hidden rounded-2xl transition-[width] duration-small ease-fluid md:flex',
        collapsed ? 'w-16' : 'w-60',
      )}
    >
      <SidebarNav
        onOpenChat={onOpenChat}
        collapsed={collapsed}
        onToggleCollapse={toggleCollapsed}
      />
    </nav>
  )
}
